import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useFamily } from '../contexts/FamilyContext';
import { motion } from 'framer-motion';
import { format, isPast, isFuture, startOfDay, endOfDay, addDays } from 'date-fns';
import { getUpcomingHolidays } from '../utils/hebrewCalendar';
import { mapHolidayToExperienceType } from '../utils/feedAlgorithm';
import { getExperienceType } from '../data/experienceTypes';
import { calculateProgress, getExperienceStatus, EXPERIENCE_STATUS } from '../utils/experiences';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles, faBullseye, faCheckCircle, faBook, faPlus, faList } from '@fortawesome/free-solid-svg-icons';
import TimelineSection from '../components/experiences/TimelineSection';
import SuggestionsSection from '../components/experiences/SuggestionsSection';
import PastExperiencesSection from '../components/experiences/PastExperiencesSection';
import QuickActions from '../components/experiences/QuickActions';
import { PageHeader, Button } from '../design-system';

export default function Experiences() {
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId, family } = useFamily();
  
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'past' | 'upcoming'

  useEffect(() => {
    if (!familyId) return;
    
    const q = query(
      collection(db, 'projects'),
      where('familyId', '==', familyId),
      orderBy('date', 'asc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const exps = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setExperiences(exps);
      setLoading(false);
    }, (error) => {
      console.error('Error loading experiences:', error);
      setLoading(false);
    });
    
    return unsubscribe;
  }, [familyId]);

  // Organize experiences by timeline
  const organizedExperiences = organizeByTimeline(experiences);
  const stats = calculateStats(experiences);
  const suggestions = generateSuggestions(family, experiences);

  const handleCreateNew = () => {
    navigate('/', { state: { openCreateModal: true } });
    // Dispatch event after a delay to ensure Dashboard is ready
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('createExperience', { detail: {} }));
    }, 500);
  };

  function StatCard({ number, label, icon, color }) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-center justify-between mb-2">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center text-white`}>
            <FontAwesomeIcon icon={icon} className="w-5 h-5" />
          </div>
        </div>
        <p className="text-3xl font-bold text-text-primary mb-1">{number}</p>
        <p className="text-sm text-text-secondary font-semibold">{label}</p>
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-8">
      <PageHeader
        title="Your Experiences"
        subtitle="A timeline of meaningful moments and celebrations"
        actions={
          <Button onClick={handleCreateNew} className="flex items-center gap-2">
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Create Experience</span>
            <span className="sm:hidden">Create</span>
          </Button>
        }
        maxWidth="1280px"
      >
        {/* Tabs - Full width border on desktop */}
        <div className="overflow-x-auto no-scrollbar border-t border-b border-border -mx-4 px-4 md:-mx-10 md:px-10">
          <div className="flex gap-2 sm:gap-3 min-w-max md:min-w-0">
            {[
              { id: 'all', label: 'All', icon: faList, count: null },
              { id: 'upcoming', label: 'Upcoming', icon: faBullseye, count: organizedExperiences.counts.upcoming },
              { id: 'past', label: 'Past', icon: faCheckCircle, count: organizedExperiences.counts.past },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`px-4 sm:px-6 py-2 text-sm sm:text-base font-semibold border-b-2 transition-colors touch-target whitespace-nowrap flex items-center gap-2 ${
                  activeFilter === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
                {tab.label}
                {tab.count !== null && tab.count > 0 && (
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
                    activeFilter === tab.id
                      ? 'bg-primary/20 text-primary'
                      : 'bg-surface text-text-secondary'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 md:pt-8">
        {loading ? (
          <LoadingSkeleton />
        ) : (
          <div className="space-y-12 md:space-y-16">
            {/* Quick Actions */}
            <QuickActions />

            {/* Suggestions Section (if no experiences or few upcoming) */}
            {(experiences.length === 0 || organizedExperiences.upcoming.length < 2) && (
              <SuggestionsSection suggestions={suggestions} onCreateNew={() => {
                navigate('/', { state: { openCreateModal: true } });
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('createExperience', { detail: {} }));
                }, 500);
              }} />
            )}

            {/* Timeline Sections */}
            {(activeFilter === 'all' || activeFilter === 'upcoming') && organizedExperiences.upcoming.length > 0 && (
              <>
                {organizedExperiences.thisWeek.length > 0 && (
                  <TimelineSection
                    title="This Week"
                    subtitle="Happening soon!"
                    icon="urgent"
                    experiences={organizedExperiences.thisWeek}
                    type="urgent"
                    highlight={true}
                  />
                )}

                {organizedExperiences.thisMonth.length > 0 && (
                  <TimelineSection
                    title="This Month"
                    subtitle={format(new Date(), 'MMMM yyyy')}
                    icon="calendar"
                    experiences={organizedExperiences.thisMonth}
                    type="upcoming"
                  />
                )}

                {organizedExperiences.later.length > 0 && (
                  <TimelineSection
                    title="Later This Year"
                    subtitle="Future celebrations"
                    icon="star"
                    experiences={organizedExperiences.later}
                    type="future"
                  />
                )}
              </>
            )}

            {/* Past Experiences */}
            {(activeFilter === 'all' || activeFilter === 'past') && organizedExperiences.past.length > 0 && (
              <>
                {/* Divider */}
                <div className="relative py-8">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t-2 border-border" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-background px-6 text-sm font-bold text-text-secondary uppercase tracking-wide">
                      Past Celebrations
                    </span>
                  </div>
                </div>

                <PastExperiencesSection experiences={organizedExperiences.pastByMonth} />
              </>
            )}

            {/* Empty State */}
            {experiences.length === 0 && !loading && (
              <EmptyState onCreateNew={() => {
                navigate('/', { state: { openCreateModal: true } });
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('createExperience', { detail: {} }));
                }, 500);
              }} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to organize experiences
function organizeByTimeline(experiences) {
  const today = startOfDay(new Date());
  const endOfWeek = endOfDay(addDays(today, 7));
  const endOfMonth = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));

  const organized = {
    thisWeek: [],
    thisMonth: [],
    later: [],
    upcoming: [],
    past: [],
    pastByMonth: {},
    counts: { upcoming: 0, past: 0 }
  };

  experiences.forEach(exp => {
    const expDate = exp.date?.toDate ? exp.date.toDate() : (exp.date ? new Date(exp.date) : null);
    if (!expDate) return;

    const status = getExperienceStatus(exp);
    const isPastExperience = isPast(expDate) || status === EXPERIENCE_STATUS.COMPLETED;

    if (isPastExperience) {
      organized.past.push(exp);
      
      // Group by month
      const monthKey = format(expDate, 'MMMM yyyy');
      if (!organized.pastByMonth[monthKey]) {
        organized.pastByMonth[monthKey] = [];
      }
      organized.pastByMonth[monthKey].push(exp);
    } else {
      organized.upcoming.push(exp);
      
      if (expDate <= endOfWeek) {
        organized.thisWeek.push(exp);
      } else if (expDate <= endOfMonth) {
        organized.thisMonth.push(exp);
      } else {
        organized.later.push(exp);
      }
    }
  });

  organized.counts.upcoming = organized.upcoming.length;
  organized.counts.past = organized.past.length;

  return organized;
}

// Calculate stats
function calculateStats(experiences) {
  const completed = experiences.filter(e => {
    const status = getExperienceStatus(e);
    return status === EXPERIENCE_STATUS.COMPLETED;
  }).length;
  const upcoming = experiences.filter(e => {
    const status = getExperienceStatus(e);
    return status === EXPERIENCE_STATUS.PLANNING || status === EXPERIENCE_STATUS.UPCOMING;
  }).length;
  const total = experiences.length;

  return { completed, upcoming, total };
}

// Generate personalized suggestions
function generateSuggestions(family, existingExperiences) {
  const suggestions = [];
  const existingTypes = new Set(existingExperiences.map(e => e.type));
  const kidsAges = family?.kids?.map(k => k.age) || [];
  const now = new Date();

  // Check upcoming holidays and suggest if not planned
  const upcomingHolidays = getUpcomingHolidays(20);
  
  upcomingHolidays.forEach(holiday => {
    const holidayType = mapHolidayToExperienceType(holiday.name);
    if (!existingTypes.has(holidayType) && holiday.daysUntil < 60 && holiday.isMajorHoliday) {
      const experienceType = getExperienceType(holidayType);
      suggestions.push({
        type: holidayType,
        title: `Plan ${holiday.name}`,
        description: `Coming up in ${holiday.daysUntil} days`,
        icon: holidayType,
        reason: 'upcoming-holiday',
        daysUntil: holiday.daysUntil
      });
    }
  });

  // Suggest Shabbat if none planned
  if (!existingTypes.has('shabbat')) {
    suggestions.push({
      type: 'shabbat',
      title: 'Start Weekly Shabbat Dinners',
      description: 'Create a meaningful Friday night tradition',
      icon: 'shabbat',
      reason: 'tradition'
    });
  }

  // Age-appropriate suggestions
  if (kidsAges.some(age => age >= 12 && age <= 14)) {
    if (!existingTypes.has('bar-mitzvah')) {
      suggestions.push({
        type: 'bar-mitzvah',
        title: 'Plan a Bar/Bat Mitzvah',
        description: 'Celebrate this important milestone',
        icon: 'bar-mitzvah',
        reason: 'age-appropriate'
      });
    }
  }

  return suggestions.slice(0, 3); // Max 3 suggestions
}

// Loading skeleton
function LoadingSkeleton() {
  return (
    <div className="space-y-8">
      {[1, 2, 3].map(i => (
        <div key={i} className="bg-white rounded-2xl border border-border p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
}

// Empty state
function EmptyState({ onCreateNew }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-16 md:py-24"
    >
      <div className="text-6xl md:text-8xl mb-6 text-primary">
        <FontAwesomeIcon icon={faWandMagicSparkles} />
      </div>
      <h2 className="text-3xl md:text-4xl font-serif font-bold text-text-primary mb-4">
        Your Journey Begins Here
      </h2>
      <p className="text-lg text-text-secondary mb-8 max-w-md mx-auto">
        Start creating meaningful Jewish experiences for your family. Every celebration tells a story.
      </p>
      <button
        onClick={onCreateNew}
        className="bg-gradient-to-r from-primary to-primary-light text-white font-bold px-8 py-4 rounded-xl hover:shadow-lg transition-all text-lg touch-target active:scale-95"
      >
        Create Your First Experience
      </button>
    </motion.div>
  );
}

